import React, { useEffect, useState } from 'react';
import CreateRuleModal from '../components/CreateRuleModal'; // adjust path if needed
import 'bootstrap/dist/css/bootstrap.min.css';
import {Rule} from '../pages/types';
import { useAuth0 } from '@auth0/auth0-react';
import { requireAccessToken } from '../utils/authToken';

const Rules: React.FC = () => {
  const APIURL = process.env.REACT_APP_API_URL;
  const [rules, setRules] = useState<Rule[]>([]);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  // for re-fetching after rule creation
  const fetchRules = async () => {
    try {
      const token = await getAccessTokenSilently();

      const res = await fetch(`${APIURL}/rules`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch rules: ${res.status}`);
      }

      const data = await res.json();
      setRules(data);
    } catch (err) {
      console.error('Error loading rules:', err);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  // Extract unique filters
  const categories = Array.from(new Set(rules.map(r => r.category)));
  const subCategories = selectedCategory
    ? Array.from(new Set(rules.filter(r => r.category === selectedCategory).map(r => r.sub_category)))
    : [];

  const topics = selectedSubCategory
    ? Array.from(new Set(rules.filter(r =>
        r.category === selectedCategory &&
        r.sub_category === selectedSubCategory
      ).map(r => r.topic)))
    : [];

  const filteredRules = rules.filter(rule =>
    (!selectedCategory || rule.category === selectedCategory) &&
    (!selectedSubCategory || rule.sub_category === selectedSubCategory) &&
    (!selectedTopic || rule.topic === selectedTopic) &&
    [rule.category, rule.sub_category, rule.topic, rule.sub_topic, rule.description, rule.exceptions]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(query.toLowerCase())
  );

  const handleDeleteRule = async (ruleId: number) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    try {
      const token = await getAccessTokenSilently();

      const res = await fetch(`${APIURL}/rules/${ruleId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error('Failed to delete rule');

      // Refresh the list
      fetchRules();
    } catch (err) {
      console.error('Error deleting rule:', err);
      alert('There was a problem deleting the rule.');
    }
  };

  return (
    <div className="bga-page" style={{ maxWidth: 1200 }}>
      <div className="bga-page-header">
        <div>
          <h1 className="bga-h1">BGA Rulebook</h1>
          <p className="bga-sub">Browse, search, and manage league rules.</p>
        </div>
        <button
          className="bga-btn bga-btn-primary"
          onClick={() => { setEditingRule(null); setShowModal(true); }}
        >
          + Create Rule
        </button>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Sidebar filters */}
        <aside className="bga-card" style={{ flex: '0 0 260px', maxWidth: '100%', alignSelf: 'stretch' }}>
          <h2 className="bga-card-title">Filter</h2>

          <div className="bga-field" style={{ marginBottom: 14 }}>
            <label className="bga-label">Category</label>
            <select
              className="bga-select"
              value={selectedCategory ?? ''}
              onChange={(e) => {
                setSelectedCategory(e.target.value || null);
                setSelectedSubCategory(null);
                setSelectedTopic(null);
              }}
            >
              <option value="">All Categories</option>
              {categories.map((cat, i) => (
                <option key={i} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {subCategories.length > 0 && (
            <div className="bga-field" style={{ marginBottom: 14 }}>
              <label className="bga-label">Subcategory</label>
              <select
                className="bga-select"
                value={selectedSubCategory ?? ''}
                onChange={(e) => {
                  setSelectedSubCategory(e.target.value || null);
                  setSelectedTopic(null);
                }}
              >
                <option value="">All Subcategories</option>
                {subCategories.map((sub, i) => (
                  <option key={i} value={sub}>{sub}</option>
                ))}
              </select>
            </div>
          )}

          {topics.length > 0 && (
            <div className="bga-field" style={{ marginBottom: 14 }}>
              <label className="bga-label">Topic</label>
              <select
                className="bga-select"
                value={selectedTopic ?? ''}
                onChange={(e) => setSelectedTopic(e.target.value || null)}
              >
                <option value="">All Topics</option>
                {topics.map((t, i) => (
                  <option key={i} value={t}>{t}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bga-field">
            <label className="bga-label">Search</label>
            <input
              className="bga-input"
              type="text"
              placeholder="Search keywords"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </aside>

        {/* Main content */}
        <main style={{ flex: '1 1 380px', minWidth: 0 }}>
          {filteredRules.length === 0 ? (
            <div className="bga-empty">No matching rules found.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {filteredRules.map(rule => (
                <div key={rule.id} className="bga-card">
                  <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
                    {rule.topic || rule.sub_category || rule.category}
                  </h2>
                  <p className="bga-muted" style={{ fontSize: 12.5, fontStyle: 'italic', margin: '4px 0 0' }}>
                    {rule.category} › {rule.sub_category}
                    {rule.sub_topic ? ` › ${rule.sub_topic}` : ''}
                  </p>

                  <div
                    style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6, color: '#2b3a32' }}
                    dangerouslySetInnerHTML={{ __html: rule.description }}
                  />

                  {rule.exceptions && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: '10px 12px',
                        background: '#fdf3f2',
                        border: '1px solid var(--bga-danger-soft)',
                        borderRadius: 8,
                      }}
                    >
                      <strong style={{ color: 'var(--bga-danger)', fontSize: 13 }}>Exceptions</strong>
                      <div
                        style={{ marginTop: 4, fontSize: 14, color: '#2b3a32' }}
                        dangerouslySetInnerHTML={{ __html: rule.exceptions }}
                      />
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <button
                      className="bga-btn bga-btn-secondary bga-btn-sm"
                      onClick={() => { setEditingRule(rule); setShowModal(true); }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="bga-btn bga-btn-danger bga-btn-sm"
                      onClick={() => handleDeleteRule(rule.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {showModal && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingRule ? 'Edit Rule' : 'Add a New Rule'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <CreateRuleModal
                    initialData={editingRule}
                    onSuccess={() => {
                      fetchRules();
                      setShowModal(false);
                    }}
                    onClose={() => setShowModal(false)}
                    getAccessTokenSilently={requireAccessToken(getAccessTokenSilently)}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Rules;
